"use client";

// src/components/SimilarProductsSection.tsx
import { useSimilarProducts } from '../hooks/useSimilarProducts';
import Link from 'next/link';
import ProductCard from './ProductCard';
import ProductCardSkeleton from './ProductCardSkeleton';
import { IoSparkles, IoChevronForward, IoChevronBack } from 'react-icons/io5';
import { useRef } from 'react';

// Import Swiper for the carousel
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

const SimilarProductsSection = ({ categoryId, currentProductId, title = "Customers Also Love" }: { categoryId?: string, currentProductId?: string, title?: string }) => {
  const { products, isLoading } = useSimilarProducts(categoryId, currentProductId);
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);

  if (!isLoading && products.length === 0) {
    return null; // Don't show the section if there's nothing to show
  }

  return (
    <div className="relative -mx-4 bg-orange-50/25 px-4 py-8">
      {/* Enhanced Heading */}
      <div className="mb-6 text-center">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary">
          <IoSparkles className="text-lg" />
          <span>Handpicked For You</span>
        </div>
        <h2 className="mb-2 text-2xl font-semibold text-accent md:text-3xl">
          {title}
        </h2>
        <p className="mx-auto max-w-2xl text-sm leading-6 text-gray-600">
          Discover more products aligned with your spiritual journey
        </p>
      </div>

      {/* Products Carousel */}
      <div className="relative group">
        {/* Navigation Buttons */}
        <button
          ref={prevRef}
          className="absolute left-0 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-gray-100 bg-white text-accent opacity-0 shadow-sm transition-all hover:bg-primary hover:text-white disabled:opacity-0 group-hover:opacity-100"
          aria-label="Previous products"
        >
          <IoChevronBack size={20} />
        </button>

        <button
          ref={nextRef}
          className="absolute right-0 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-gray-100 bg-white text-accent opacity-0 shadow-sm transition-all hover:bg-primary hover:text-white disabled:opacity-0 group-hover:opacity-100"
          aria-label="Next products"
        >
          <IoChevronForward size={20} />
        </button>

        <Swiper
          modules={[Navigation, Autoplay]}
          spaceBetween={16}
          slidesPerView={'auto'}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          onBeforeInit={(swiper) => {
            if (typeof swiper.params.navigation !== 'boolean') {
              const navigation = swiper.params.navigation;
              if (navigation) {
                navigation.prevEl = prevRef.current;
                navigation.nextEl = nextRef.current;
              }
            }
          }}
          autoplay={{
            delay: 4000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          breakpoints={{
            320: { slidesPerView: 1.2 },
            480: { slidesPerView: 1.5 },
            640: { slidesPerView: 2 },
            768: { slidesPerView: 2.5 },
            1024: { slidesPerView: 3 },
            1280: { slidesPerView: 4 },
          }}
          className="!py-4"
        >
          {isLoading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <SwiperSlide key={index}>
                <ProductCardSkeleton />
              </SwiperSlide>
            ))
          ) : (
            products.map(product => (
              <SwiperSlide key={product.id}>
                <ProductCard product={product} />
              </SwiperSlide>
            ))
          )}
        </Swiper>
      </div>

      {/* Call to Action */}
      {!isLoading && products.length > 0 && (
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500 mb-3">
            <span className="font-semibold text-primary">{products.length}+ similar products</span> waiting to enhance your spiritual journey
          </p>
          <Link
            href="/products"
            className="inline-flex items-center gap-1 text-primary font-semibold text-sm hover:underline hover:gap-2 transition-all"
          >
            View More <IoChevronForward className="text-base" />
          </Link>
        </div>
      )}
    </div>
  );
};

export default SimilarProductsSection;
